import { invokeAsExtension } from './enforce';
import {
  listLiveDbConnections,
  liveDbConnectionEngine,
  readSavedDbConnection,
  connectDbFromSavedConfig,
  markDbConnectionClosed,
} from './db';
import { isDbManager, isDbVaultLocked } from './dbVaultConnections';
import { DB_PING_COMMANDS, DB_MANAGER_EXTENSION_ID } from '../dbEngines';
import { getBridge } from '../store';
import { useTabsStore, setDbManagerTabNeedsReconnect } from '../../store/tabsStore';

/**
 * Notices when a database connection has died, and quietly puts it back.
 *
 * Until this existed, death was discovered by a query failing. That is the
 * worst possible moment: the user has already typed something, hit Run, and
 * waited — and the most common cause is not a real fault but a laptop that
 * slept, which resolves itself the instant anyone redials.
 *
 * So: ping on a timer, and on window focus / visibilitychange, which is the
 * practical resume-from-sleep signal. A ping is not a query — it takes no pool
 * slot when the pool is busy — so this cannot become the cause of the stall it
 * is watching for.
 *
 * The rule it follows: redial silently for as long as the stored credentials
 * allow it, and flip the tab to its Connect screen the moment they do not.
 * Reconnecting without a remembered password would send an empty one, and the
 * user would get an authentication failure they did not ask for instead of the
 * prompt they needed.
 */

const DEFAULT_INTERVAL_MS = 60_000;

// 1s, 5s, 15s. Three attempts, because the case this is for — a machine that
// just woke — is usually fixed by the first and never by the tenth. Backing off
// further would only delay the honest "reconnect" screen.
export const REDIAL_BACKOFF_MS = [1000, 5000, 15000];

const connKey = (extensionId, connId) => `${extensionId}:${connId}`;

/**
 * Tell the DB Manager sidebar its answer changed. The guest re-asks
 * `db.listSavedConnections`, which on a locked vault answers `vaultLocked` —
 * and its tree then shows the existing "Vault locked / Unlock" row instead of
 * connection rows (the same push hostBridge sends on unlock, in the other
 * direction). Best-effort: an idle-torn-down sidebar re-asks on activation
 * anyway.
 */
function defaultNotifyVaultLocked() {
  getBridge(DB_MANAGER_EXTENSION_ID)?.postToGuest('__db_connections_changed__', {});
}

/**
 * Start monitoring. Returns a stop() — call it on teardown, or the timer and
 * the listeners outlive the session that owns them.
 */
export function startDbHealthMonitor({
  intervalMs = DEFAULT_INTERVAL_MS,
  // Seams, so the tests do not need real timers or a real backend.
  ping = defaultPing,
  // Never opens the Vault gate: this runs on a timer, nobody clicked.
  redial = (extensionId, connId) => connectDbFromSavedConfig(extensionId, connId, { interactive: false }),
  readSaved = readSavedDbConnection,
  listLive = listLiveDbConnections,
  isVaultLocked = isDbVaultLocked,
  notifyVaultLocked = defaultNotifyVaultLocked,
} = {}) {
  // In-flight redials, so a focus event during a sweep does not start a second
  // one for the same connection.
  const recovering = new Set();
  let stopped = false;

  async function checkOne({ extensionId, connId }) {
    const key = connKey(extensionId, connId);
    if (recovering.has(key)) return;

    try {
      await ping(extensionId, connId);
      return; // alive; nothing to do, and nothing to log
    } catch {
      // Fall through to recovery.
    }

    recovering.add(key);
    try {
      await recover(extensionId, connId);
    } finally {
      recovering.delete(key);
    }
  }

  /**
   * True when this connection's credentials sit behind a vault that is
   * currently locked. Two gates before the status round-trip: only the DB
   * Manager stores connections in the vault at all, and even then a legacy
   * descriptor — read from extension storage, password in the OS keychain, a
   * state db.js still supports — is untouched by a lock and must keep its
   * full redial through one. Keying on the extension id alone stripped every
   * pre-migration connection of its backoff whenever the vault was locked,
   * which auto-lock makes the steady state.
   *
   * `saved: null` does NOT clear the origin gate: while locked, a vault-backed
   * read answers null (the storage fallback has no row for a vault id), so an
   * unreadable descriptor plus a locked vault means "behind the lock", not
   * "nothing saved".
   */
  async function lockedBehindVault(extensionId, saved) {
    // A legacy connection's remembered password is in the Vault too now
    // (secureStorage, 26.10.2026), whichever extension saved it.
    const legacySecret = !!saved && saved.origin !== 'vault' && saved.engine !== 'sqlite' && !!saved.remember;
    if (!legacySecret && !isDbManager(extensionId)) return false;
    if (!legacySecret && saved && saved.origin !== 'vault') return false;
    try {
      return await isVaultLocked();
    } catch {
      return false;
    }
  }

  /**
   * A locked vault is not a dead server, and no amount of backoff cures it —
   * only a human with the passphrase can. So: stop pretending the connection
   * is live (tabs get their Connect screen exactly as giveUp does), and tell
   * the sidebar, whose locked row says "unlock" instead of implying the
   * database went away.
   */
  function giveUpVaultLocked(extensionId, connId) {
    giveUp(extensionId, connId);
    notifyVaultLocked();
  }

  async function recover(extensionId, connId) {
    // Read FIRST: the descriptor's origin is what says whether a locked vault
    // is even this connection's problem (see lockedBehindVault). The read is
    // safe to do before the lock check — on a locked vault it answers null,
    // and null-plus-locked is treated as "behind the lock" below.
    const saved = await readSaved(extensionId, connId).catch(() => null);

    // Checked before the eligibility test: a locked vault makes a vault-backed
    // readSavedDbConnection answer null, which the eligibility test would
    // misreport as "connection failed / nothing saved" — the exact wrong
    // message for someone whose credentials are merely behind a lock
    // (auto-lock plan A5). This is also the lock-on-suspend path: the machine
    // wakes with the connection dead AND the vault locked.
    if (await lockedBehindVault(extensionId, saved)) {
      giveUpVaultLocked(extensionId, connId);
      return;
    }

    // A password we cannot supply means the user has to. SQLite has no
    // credentials at all, so it is always eligible.
    const eligible = !!saved && (saved.engine === 'sqlite' || saved.remember);
    if (!eligible) {
      giveUp(extensionId, connId);
      return;
    }

    for (const wait of REDIAL_BACKOFF_MS) {
      if (stopped) return;
      await delay(wait);
      if (stopped) return;
      try {
        const engine = await redial(extensionId, connId);
        // The vault can lock mid-backoff (the timer does not wait for us),
        // and for a vault-backed connection that surfaces as a null resolve —
        // connectDbFromSavedConfig reads "no usable saved config" — not as a
        // throw. Burning the remaining steps against a lock only a human can
        // clear would just delay the honest answer.
        if (engine == null && (await lockedBehindVault(extensionId, saved))) {
          giveUpVaultLocked(extensionId, connId);
          return;
        }
        return; // back up; connectDbFromSavedConfig re-marks it live
      } catch {
        // Same mid-backoff lock, for a redial that rejects instead.
        if (await lockedBehindVault(extensionId, saved)) {
          giveUpVaultLocked(extensionId, connId);
          return;
        }
        // Try the next backoff step.
      }
    }
    giveUp(extensionId, connId);
  }

  /**
   * Stop pretending it is connected: drop it from the live registry and put
   * every tab on that connection back to its Connect screen.
   */
  function giveUp(extensionId, connId) {
    markDbConnectionClosed(extensionId, connId);
    // Only the tabs on THIS connection. The store setter is per-tab, and
    // flagging every DB tab would send someone to a Connect screen for a
    // database that never went anywhere.
    const tabs = useTabsStore.getState().dbManagerTabs || [];
    for (const tab of tabs) {
      if (tab.extensionId === extensionId && tab.connId === connId) {
        setDbManagerTabNeedsReconnect(tab.id);
      }
    }
  }

  async function sweep() {
    if (stopped) return;
    const live = listLive();
    // Sequential rather than parallel: a sweep that finds several dead
    // connections would otherwise fire every redial at a server that is very
    // likely still coming back up.
    for (const conn of live) {
      if (stopped) return;
      // eslint-disable-next-line no-await-in-loop
      await checkOne(conn);
    }
  }

  const onWake = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    sweep();
  };

  const timer = setInterval(sweep, intervalMs);
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', onWake);
    window.addEventListener('visibilitychange', onWake);
  }

  return function stop() {
    stopped = true;
    clearInterval(timer);
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', onWake);
      window.removeEventListener('visibilitychange', onWake);
    }
  };
}

/** Rejects when the connection is not usable, which is all the caller needs. */
async function defaultPing(extensionId, connId) {
  // The engine comes from the live registry (recorded at connect time), never
  // from readSavedDbConnection: that read lands on vault_get_item, which
  // stamps vault auto-lock activity — and a 60-second heartbeat that stamps
  // activity is a vault that never idles, so M1 would silently never fire
  // while any DB connection was live. The registry also answers while the
  // vault is locked, which is what lets a connection that died during a
  // lock-on-suspend be noticed and surfaced as "vault locked" at all,
  // instead of reported alive because its engine could not be learned.
  const command = DB_PING_COMMANDS[liveDbConnectionEngine(extensionId, connId)];
  // An unknown engine cannot be pinged, and treating that as death would flip
  // a working tab to its Connect screen for no reason.
  if (!command) return true;
  return invokeAsExtension(command, { extensionId, connId });
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
